import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeItem, setActiveItem] = useState("Home");
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { name: "Home", href: "/", type: "route" },
    { name: "Services", href: "/services", type: "route" },
    { name: "Solutions", href: "/solutions", type: "route" },
    { name: "Pricing", href: "/pricing", type: "route" },
    { name: "About", href: "/about", type: "route" },
    { name: "Contact", href: "/contact", type: "route" }
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-lg shadow-lg py-2"
          : "bg-transparent py-4"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          {/* Logo with animation */}
          <div className="flex items-center">
            <Link to="/" className="relative group">
              <span className="text-indigo-600 font-bold text-2xl tracking-tight transition-all duration-300 group-hover:scale-110 group-hover:text-indigo-700 cursor-pointer">
                AdBlastX
              </span>
              <div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 group-hover:w-full transition-all duration-300"></div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navItems.map((item) => {
              const isActive = item.type === "route" 
                ? location.pathname === item.href 
                : activeItem === item.name;
              
              const NavElement = item.type === "route" ? Link : "a";
              const navProps = item.type === "route" 
                ? { to: item.href } 
                : { href: item.href };

              return (
                <NavElement
                  key={item.name}
                  {...navProps}
                  className="relative group"
                  onClick={() => setActiveItem(item.name)}
                >
                  <span
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 transform group-hover:scale-105 ${
                      isActive
                        ? "text-indigo-600 bg-indigo-50"
                        : "text-gray-600 hover:text-indigo-600"
                    }`}
                  >
                    {item.name}
                  </span>
                  
                  {/* Animated underline */}
                  <div
                    className={`absolute bottom-0 left-1/2 w-0 h-0.5 bg-gradient-to-r from-indigo-600 to-purple-600 transition-all duration-300 group-hover:w-4/5 group-hover:left-2 ${
                      isActive ? "w-4/5 left-2" : ""
                    }`}
                  ></div>
                  
                  {/* Hover effect background */}
                  <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-indigo-50 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
                </NavElement>
              );
            })}
          </div>

          {/* CTA Button with enhanced animation */}
          <div className="hidden md:block">
            <button className="relative overflow-hidden group bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 py-2.5 rounded-xl text-sm font-medium transition-all duration-300 transform hover:scale-105 hover:shadow-lg hover:shadow-indigo-500/25">
              <span className="relative z-10">Get Started</span>
              
              {/* Shine effect */}
              <div className="absolute inset-0 -left-full group-hover:left-full w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-all duration-700"></div>
              
              {/* Pulse animation */}
              <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden relative w-8 h-8 flex flex-col justify-center items-center group"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <span
              className={`w-6 h-0.5 bg-gray-600 rounded-full transition-all duration-300 ${
                isMobileMenuOpen ? "rotate-45 translate-y-0" : "-translate-y-1"
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-gray-600 rounded-full transition-all duration-300 mt-1 ${
                isMobileMenuOpen ? "opacity-0" : "opacity-100"
              }`}
            ></span>
            <span
              className={`w-6 h-0.5 bg-gray-600 rounded-full transition-all duration-300 mt-1 ${
                isMobileMenuOpen ? "-rotate-45 -translate-y-2" : "translate-y-1"
              }`}
            ></span>
          </button>
        </div>

        {/* Mobile Menu */}
        <div
          className={`md:hidden overflow-hidden transition-all duration-500 ${
            isMobileMenuOpen ? "max-h-96 opacity-100 py-4" : "max-h-0 opacity-0"
          }`}
        >
          <div className="flex flex-col space-y-3">
            {navItems.map((item) => {
              const isActive = item.type === "route" 
                ? location.pathname === item.href 
                : activeItem === item.name;
              
              const NavElement = item.type === "route" ? Link : "a";
              const navProps = item.type === "route" 
                ? { to: item.href } 
                : { href: item.href };

              return (
                <NavElement
                  key={item.name}
                  {...navProps}
                  className={`px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 transform hover:translate-x-2 ${
                    isActive
                      ? "text-indigo-600 bg-indigo-50 border-l-4 border-indigo-600"
                      : "text-gray-600 hover:text-indigo-600 hover:bg-gray-50"
                  }`}
                  onClick={() => {
                    setActiveItem(item.name);
                    setIsMobileMenuOpen(false);
                  }}
                >
                  {item.name}
                </NavElement>
              );
            })}
            <button className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 rounded-xl text-base font-medium transition-all duration-300 transform hover:scale-105 mt-2">
              Get Started
            </button>
          </div>
        </div>
      </div>

      {/* Background blur overlay for mobile */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        ></div>
      )}
    </nav>
  );
};

export default Navbar;